import * as React from "react";
import type { Initiative, Person, Update } from "@/lib/types";
import { TEAMS } from "@/lib/constants";
import { useRecentUpdates } from "@/lib/store";
import { extractUpdateSummary, renderInline } from "@/lib/update-markdown";
import { StatusPill, Avatar } from "./primitives";

// Monday-start ISO week. Update timestamps are real time, so we anchor on the
// real current week (not the app's frozen TODAY, which is a prototype anchor).
function startOfISOWeek(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7; // Mon=0 … Sun=6
  x.setDate(x.getDate() - day);
  return x;
}

function fmtShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Only in-flight work is expected to report weekly.
const ACTIVE_STATUSES = new Set<string>(["not-started", "in-progress"]);

export function WeeklyReview({
  initiatives,
  roster,
  onOpen,
}: {
  initiatives: Initiative[];
  roster: Person[];
  onOpen: (i: Initiative) => void;
}) {
  const weekStart = React.useMemo(() => startOfISOWeek(new Date()), []);
  // Fetch a wider window so "last update" on quiet initiatives has context.
  const sinceISO = React.useMemo(() => {
    const s = new Date(weekStart);
    s.setDate(s.getDate() - 56);
    return s.toISOString();
  }, [weekStart]);

  const { updates } = useRecentUpdates(sinceISO);

  // Per initiative: the latest update overall, and the latest one this week.
  const byInitiative = React.useMemo(() => {
    const map = new Map<string, { latest?: Update; thisWeek?: Update }>();
    const wk = weekStart.getTime();
    const sorted = [...updates].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    for (const u of sorted) {
      const e = map.get(u.initiativeId) ?? {};
      if (!e.latest) e.latest = u;
      if (!e.thisWeek && new Date(u.createdAt).getTime() >= wk) e.thisWeek = u;
      map.set(u.initiativeId, e);
    }
    return map;
  }, [updates, weekStart]);

  const active = React.useMemo(
    () => initiatives.filter(i => !i.archivedAt && ACTIVE_STATUSES.has(i.status)),
    [initiatives]
  );

  const authorPerson = (id: string | null | undefined) => (id ? roster.find(p => p.id === id) : undefined);
  const authorName = (id: string | null | undefined) => authorPerson(id)?.name ?? "Someone";

  if (active.length === 0) {
    return (
      <div className="empty-shell" style={{ marginTop: 32 }}>
        <h3>No active initiatives.</h3>
        <p>Once initiatives are in progress, owners can post weekly updates and they’ll roll up here.</p>
      </div>
    );
  }

  const updatedCount = active.filter(i => byInitiative.get(i.id)?.thisWeek).length;
  const pct = Math.round((updatedCount / active.length) * 100);
  const weekLabel = weekStart.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  // Grouped by team — an initiative appears exactly once (the old objective
  // grouping duplicated multi-objective initiatives). Stray teamIds (removed
  // from TEAMS) fall into an "Other" bucket so nothing silently vanishes.
  const teamGroups = TEAMS
    .map(t => ({ id: t.id, name: t.name, items: active.filter(i => i.teamId === t.id) }));
  const stray = active.filter(i => !TEAMS.some(t => t.id === i.teamId));
  const groups = [
    ...teamGroups,
    ...(stray.length ? [{ id: "other", name: "Other", items: stray }] : []),
  ].filter(g => g.items.length > 0);

  return (
    <div className="weekly-review">
      <div className="wr-head">
        <div className="wr-title">
          <h3>Weekly Review</h3>
          <span className="wr-week">Week of {weekLabel}</span>
        </div>
        <div className="wr-rollup">
          <span className="wr-big">{updatedCount}</span>
          <span className="wr-small">of {active.length} active initiatives updated this week</span>
          <span className="wr-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
        </div>
      </div>

      {groups.map(({ id, name, items }) => {
        const done = items.filter(i => byInitiative.get(i.id)?.thisWeek).length;
        return (
          <div className="wr-group" key={id}>
            <div className="wr-group-h">
              <span className="wr-name">{name}</span>
              <span className="wr-tally">{done} of {items.length} updated</span>
            </div>
            {items.map(i => {
              const entry = byInitiative.get(i.id);
              const u = entry?.thisWeek;
              const author = u ? authorPerson(u.authorId) : undefined;
              return (
                <button type="button" className={`wr-init ${u ? "" : "missing"}`} key={i.id} onClick={() => onOpen(i)}>
                  <span className="wr-nm">{i.name}</span>
                  <span className="wr-right"><StatusPill status={i.status} /></span>
                  {u ? (
                    <>
                      {/* Progress digest for convention-followers, first line otherwise.
                          links:false — this row is a <button>, an <a> inside it is invalid. */}
                      <span className="wr-upd">{renderInline(extractUpdateSummary(u.body), { links: false })}</span>
                      <span className="wr-attr">
                        {author && <Avatar person={author} size="sm" />}
                        <span>{authorName(u.authorId)} · {fmtShort(u.createdAt)}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="wr-upd"><span className="wr-flag">No update this week</span></span>
                      <span className="wr-attr">{entry?.latest ? `Last update: ${fmtDay(entry.latest.createdAt)}` : "No updates yet"}</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
